import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, Container, Grid, Stack } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  AutoGraph as AutoGraphIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import VerenaChat from './VerenaChat';
import SEOHead from '../SEO/SEOHead';

const Contact = () => {
  return (
    <Box sx={{
      height: '100vh',
      bgcolor: '#050505',
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      <SEOHead
        title="Contact VerveAI — Start Your 48-Hour Digital Launchpad"
        description="Get in touch with the VerveAI launch team. Inquire about turnkey startup setup, 2–4 week MVP builds, or schedule a free discovery consultation."
        canonical="https://verveai.co/contact"
      />
      {/* Background Ambience */}
      <Box sx={{
        position: 'absolute',
        top: '-20%',
        left: '-10%',
        width: '50%',
        height: '50%',
        background: 'radial-gradient(circle, rgba(0,122,255,0.15) 0%, rgba(0,0,0,0) 70%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: '-20%',
        right: '-10%',
        width: '50%',
        height: '50%',
        background: 'radial-gradient(circle, rgba(0,230,118,0.1) 0%, rgba(0,0,0,0) 70%)',
        zIndex: 0,
        pointerEvents: 'none'
      }} />

      <Grid container sx={{ height: '100%', zIndex: 1 }}>

        {/* Left Side: Value Prop */}
        <Grid item xs={12} md={5} lg={5} sx={{
          p: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          borderRight: { md: '1px solid rgba(255,255,255,0.05)' },
          bgcolor: 'rgba(0,0,0,0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <Box>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Typography variant="h6" sx={{ color: '#007AFF', mb: 6, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                <ArrowBackIcon fontSize="small" /> Back to Home
              </Typography>
            </Link>

            <Typography variant="h1" sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '3rem', md: '4rem', lg: '5rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: '#fff' // Fallback
            }}>
              <Box component="span" sx={{
                background: 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'block'
              }}>
                Talk to the
              </Box>
              <Box component="span" sx={{
                background: 'linear-gradient(135deg, #2196F3 30%, #21CBF3 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'block'
              }}>
                Future of Sales.
              </Box>
            </Typography>

            <Typography variant="h6" sx={{ color: '#94a3b8', mb: 6, maxWidth: '500px', lineHeight: 1.6, fontWeight: 400, fontSize: '1.25rem' }}>
              Verena is our advanced AI agent designed to analyze your business bottlenecks and architect a custom automation strategy in minutes.
            </Typography>

            <Stack spacing={4}>
              {[
                { icon: <TimelineIcon sx={{ color: '#00E676', fontSize: 28 }} />, text: "Instant Workflow Analysis" },
                { icon: <AutoGraphIcon sx={{ color: '#007AFF', fontSize: 28 }} />, text: "ROI Projection & Strategy" },
                { icon: <SecurityIcon sx={{ color: '#FFD700', fontSize: 28 }} />, text: "Enterprise-Grade Security" }
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '16px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ color: '#e2e8f0', fontSize: '1.2rem', fontWeight: 500 }}>{item.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>

        {/* Right Side: Chat Interface */}
        <Grid item xs={12} md={7} lg={7} sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 6 },
          position: 'relative'
        }}>
          <Box sx={{
            width: '100%',
            maxWidth: '900px',
            height: '100%',
            maxHeight: '85vh',
            bgcolor: 'rgba(15, 20, 25, 0.7)',
            backdropFilter: 'blur(40px)',
            borderRadius: '32px',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Window Controls Decoration */}
            <Box sx={{
              p: 2.5,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(0,0,0,0.2)',
              gap: 1.5
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#FF5F56', borderRadius: '50%' }} />
                <Box sx={{ width: 12, height: 12, bgcolor: '#FFBD2E', borderRadius: '50%' }} />
                <Box sx={{ width: 12, height: 12, bgcolor: '#27C93F', borderRadius: '50%' }} />
              </Box>
              <Typography sx={{ ml: 2, fontSize: '0.85rem', color: '#64748B', fontWeight: 600, letterSpacing: '0.05em' }}>
                VERENA_AGENT_V2.0
              </Typography>
              <Box sx={{ ml: 'auto', px: 2, py: 0.5, borderRadius: '20px', bgcolor: 'rgba(0,230,118,0.1)', border: '1px solid rgba(0,230,118,0.2)' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#00E676', fontWeight: 700 }}>
                  ONLINE
                </Typography>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <VerenaChat />
            </Box>
          </Box>
        </Grid>

      </Grid>
    </Box>
  );
};

export default Contact;
